'use client';
import React, { useState } from 'react';
import { Shield, RefreshCw, Filter, Search, ChevronDown, Briefcase } from 'lucide-react';
import AdminKPIs from './AdminKPIs';
import AdminExpertTable from './AdminExpertTable';
import AdminEngagementsTab from './AdminEngagementsTab';
import { toast } from 'sonner';

type ActiveTab = 'experts' | 'engagements';

export default function AdminPanelContent() {
  const [recalculating, setRecalculating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [activeTab, setActiveTab] = useState<ActiveTab>('experts');

  const handleRecalculate = async () => {
    setRecalculating(true);
    await new Promise((r) => setTimeout(r, 1800));
    setRecalculating(false);
    toast?.success('Badge tiers recalculated for all experts');
  };

  return (
    <div className="py-8 flex flex-col gap-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Shield size={16} className="text-secondary" />
            </div>
            <h1 className="font-heading text-display text-foreground">Admin Panel</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Review and approve expert profiles. Manage platform quality and badge tiers.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-xs font-mono-data text-red-400">Access restricted to admin@expertvault.io</span>
          </div>
        </div>

        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-secondary/30 bg-secondary/5 text-secondary text-sm font-medium hover:bg-secondary/10 active:scale-95 transition-all duration-150 disabled:opacity-60"
        >
          <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} />
          {recalculating ? 'Recalculating…' : 'Recalculate Badge Tiers'}
        </button>
      </div>

      <AdminKPIs />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b border-border">
        <button
          onClick={() => setActiveTab('experts')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'experts' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Shield size={14} />
          Expert Review Queue
        </button>
        <button
          onClick={() => setActiveTab('engagements')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'engagements' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Briefcase size={14} />
          Engagements
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'experts' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-heading text-lg text-foreground">Expert Review Queue</h2>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search by pseudonym or domain…"
                  className="bg-input border border-border rounded-lg pl-8 pr-4 py-2 text-sm text-foreground placeholder-muted-foreground outline-none focus:border-primary/50 transition-colors w-64"
                  aria-label="Search experts"
                />
              </div>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value)}
                  className="appearance-none bg-input border border-border rounded-lg pl-3 pr-8 py-2 text-sm text-foreground outline-none focus:border-primary/50 transition-colors cursor-pointer"
                  aria-label="Filter by status"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="WEB_CHECKED">Web Checked</option>
                  <option value="FLAGGED">Flagged</option>
                </select>
                <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>
          <AdminExpertTable searchQuery={searchQuery} statusFilter={statusFilter} />
        </div>
      )}

      {activeTab === 'engagements' && <AdminEngagementsTab />}
    </div>
  );
}